import { useMemo, useRef, useState } from "react";
import { randomId } from "@/shared/utils/randomId";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useNotification } from "@/shared/hooks/useNotification";
import { useApiError } from "@/shared/hooks/useApiError";
import { useUnitsOfMeasure } from "@/shared/hooks/useUnitsOfMeasure";
import { useReturnRequisitionItem } from "../../hooks/useRequisition";
import type {
  Requisition,
  RequisitionItem,
} from "../../services/requisitionApi";

interface RequisitionReturnDialogProps {
  requisition: Requisition | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface ItemDraft {
  quantity: string;
  unitName: string;
  notes: string;
}

export const RequisitionReturnDialog = ({
  requisition,
  open,
  onOpenChange,
  onSuccess,
}: RequisitionReturnDialogProps) => {
  const submit = useReturnRequisitionItem();
  const returnKeys = useRef<Record<string, string>>({});
  const notify = useNotification();
  const handleApiError = useApiError();
  const unitsQuery = useUnitsOfMeasure();

  const [drafts, setDrafts] = useState<Record<string, ItemDraft>>({});
  // Physical hand-back date — defaults to today, applied to every row submitted
  // from this dialog. Lets users back-date returns when the system entry lags.
  const [returnedDate, setReturnedDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );

  // Items eligible for return — those that have been issued. Compute the
  // already-returned and pending-return totals so the user sees what's still available.
  const returnableItems = useMemo(() => {
    if (!requisition) return [] as Array<RequisitionItem & {
      remainingNative: number;
    }>;
    return (requisition.items ?? [])
      .filter((it) => (it.quantityIssued ?? 0) > 0 && !!it.id)
      .map((it) => {
        const issued = it.quantityIssued ?? 0;
        const returned = it.quantityReturned ?? 0;
        return { ...it, remainingNative: Math.max(0, issued - returned - (it.quantityConsumed ?? 0)) };
      });
  }, [requisition]);

  const updateDraft = (id: string, patch: Partial<ItemDraft>) =>
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        quantity: prev[id]?.quantity ?? "",
        unitName: prev[id]?.unitName ?? "",
        notes: prev[id]?.notes ?? "",
        ...patch,
      },
    }));

  const handleSubmit = async () => {
    if (!requisition) return;

    // Collect submissions for items where the user typed a positive quantity.
    const submissions = returnableItems
      .map((it) => {
        const d = drafts[it.id!];
        const qty = parseFloat(d?.quantity ?? "");
        if (isNaN(qty) || qty <= 0) return null;
        const unitName = d?.unitName || it.unitOfMeasure;
        const unitMeta = unitsQuery.data?.find((u) => u.name === unitName);
        if (!unitMeta) return null;
        return {
          itemId: it.id!,
          inputName: it.inputName ?? "Item",
          requisitionId: requisition.id,
          quantityReturned: qty,
          unitOfMeasure: unitMeta.value,
          notes: d?.notes?.trim() || undefined,
          returnedDate: returnedDate
            ? new Date(returnedDate).toISOString()
            : undefined,
        };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    if (submissions.length === 0) {
      notify.warning("Enter a quantity on at least one item before submitting.");
      return;
    }

    let succeeded = 0;
    let failed = 0;
    for (const s of submissions) {
      try {
        await submit.mutateAsync({
          idempotencyKey: returnKeys.current[s.itemId] ??= randomId(),
          requisitionId: s.requisitionId,
          itemId: s.itemId,
          quantityReturned: s.quantityReturned,
          unitOfMeasure: s.unitOfMeasure,
          notes: s.notes,
          returnedDate: s.returnedDate,
        });
        delete returnKeys.current[s.itemId];
        succeeded++;
      } catch (e) {
        failed++;
        handleApiError(e);
      }
    }

    if (succeeded > 0) {
      notify.success(
        `${succeeded} return${succeeded > 1 ? "s" : ""} submitted — awaiting store-manager approval.${
          failed > 0 ? ` ${failed} failed.` : ""
        }`
      );
      setDrafts({});
      onSuccess?.();
      if (failed === 0) onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto"
        style={{ width: "780px", maxWidth: "95vw" }}
      >
        <DialogHeader>
          <DialogTitle>Return Items</DialogTitle>
          <DialogDescription>
            {requisition
              ? `Submit a return for ${requisition.purpose}. Each row is a separate return that the store manager will approve before stock is credited back.`
              : "—"}
          </DialogDescription>
        </DialogHeader>

        {requisition && (
          <div className="space-y-3">
            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground flex items-center justify-between">
              <span>
                Requisition{" "}
                <span className="font-medium text-foreground">
                  {requisition.requisitionNumber ?? requisition.id.slice(0, 8)}
                </span>
              </span>
              <span>{requisition.departmentName}</span>
            </div>

            <Field>
              <FieldLabel>Returned date</FieldLabel>
              <DatePicker
                value={returnedDate}
                onChange={setReturnedDate}
                disabled={submit.isPending}
              />
            </Field>

            {returnableItems.length === 0 ? (
              <div className="text-sm text-muted-foreground py-8 text-center border rounded">
                Nothing to return — no items have been issued yet on this
                requisition.
              </div>
            ) : (
              <div className="rounded border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Issued</TableHead>
                      <TableHead className="text-right">Already returned</TableHead>
                      <TableHead className="text-right">Available</TableHead>
                      <TableHead className="w-44">Return qty</TableHead>
                      <TableHead className="w-44">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {returnableItems.map((it) => {
                      const draft = drafts[it.id!] ?? {
                        quantity: "",
                        unitName: "",
                        notes: "",
                      };
                      const unitName = draft.unitName || it.unitOfMeasure;
                      const unitMeta = unitsQuery.data?.find(
                        (u) => u.name === it.unitOfMeasure
                      );
                      const compatible =
                        unitsQuery.data?.filter(
                          (u) => u.category === unitMeta?.category
                        ) ?? [];
                      const qty = parseFloat(draft.quantity) || 0;
                      const overLimit = qty > it.remainingNative;
                      return (
                        <TableRow key={it.id}>
                          <TableCell>
                            <div className="font-medium">{it.inputName}</div>
                            {it.isExternalItem && (
                              <Badge variant="outline" className="mt-1 text-[10px]">
                                External procurement
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {it.quantityIssued ?? 0} {it.unitOfMeasure}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {it.quantityReturned ?? 0} {it.unitOfMeasure}
                          </TableCell>
                          <TableCell className="text-right text-sm font-semibold">
                            {it.remainingNative.toFixed(2)} {it.unitOfMeasure}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                value={draft.quantity}
                                onChange={(e) =>
                                  updateDraft(it.id!, { quantity: e.target.value })
                                }
                                disabled={submit.isPending}
                                className={
                                  overLimit ? "border-red-300" : undefined
                                }
                              />
                              <Select
                                value={unitName}
                                onValueChange={(v) =>
                                  updateDraft(it.id!, { unitName: v })
                                }
                                disabled={submit.isPending}
                              >
                                <SelectTrigger className="w-20">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {compatible.map((u) => (
                                    <SelectItem key={u.value} value={u.name}>
                                      {u.abbreviation || u.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {overLimit && (
                              <p className="text-xs text-red-600 mt-1">
                                Exceeds available
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={draft.notes}
                              onChange={(e) =>
                                updateDraft(it.id!, { notes: e.target.value })
                              }
                              placeholder="Reason / context"
                              disabled={submit.isPending}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Submitted returns are <span className="font-medium">pending</span>{" "}
              until the store manager approves. Stock isn't credited until then.
            </p>

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={submit.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  submit.isPending || returnableItems.length === 0
                }
              >
                {submit.isPending ? "Submitting..." : "Submit returns"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
